import React from "react";
import { Plus } from "lucide-react";
import { motion } from "framer-motion";
import { SectionBadge, IconSparkle, Highlight, FadeUp } from "@/components/BrandKit";
import { DoodleBeaker, DoodleStar, DoodleArrow, DoodleFace } from "@/components/Doodles";

const SERVICES = [
  { no: "01", title: "360° Video Campaigns", desc: "Platform agnostic, scale ready films built for everywhere your audience lives." },
  { no: "02", title: "AI Native Content", desc: "Fast moving, high volume AI films made without sacrificing human point of view." },
  { no: "03", title: "Narrative Led Brand Films", desc: "Emotion first films that give brands memory, meaning, and main character energy." },
  { no: "04", title: "Multilingual Campaigns", desc: "Hyperlocal campaigns that speak fluent India." },
  { no: "05", title: "Product & Category Films", desc: "Sharp, functional films that make products feel dangerously add to cart able." },
  { no: "06", title: "Influencer & Creator Films", desc: "Creator powered content that feels native to the internet." },
  { no: "07", title: "AI Storyboarding & Pre-Viz", desc: "Concepts, frames, worlds, and visual routes mapped pre production." },
  { no: "08", title: "Experimental R&D Sandbox", desc: "A playground for new formats, strange ideas, emerging tools, and ambitious campaigns." },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="border-b border-black relative"
    >
      <div className="max-w-[1480px] mx-auto px-5 sm:px-8 py-16 sm:py-20">
        {/* Header row w/ doodle illustration */}
        <div className="grid grid-cols-12 gap-8 items-start mb-6 sm:mb-8">
          <div className="col-span-12 md:col-span-7">
            <FadeUp>
              <SectionBadge
                label="Services"
                num="02"
                color="#FF4D00"
                icon={<IconSparkle className="w-4 h-4 text-white" />}
              />
            </FadeUp>
            <FadeUp delay={0.08}>
              <h2
                data-testid="services-heading"
                className="mt-5 font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight"
                style={{ fontWeight: 900 }}
              >
                Things we&apos;re{" "}
                <Highlight color="#FF4D00">good</Highlight> at.
              </h2>
            </FadeUp>
          </div>

          {/* Chunky doodle illustration cluster */}
          <div className="col-span-12 md:col-span-5 relative flex justify-center md:justify-end">
            <FadeUp delay={0.15}>
              <div className="relative w-full max-w-[280px] h-[220px]">
                <motion.div
                  animate={{ rotate: [0, -3, 3, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute right-4 top-2 text-black"
                >
                  <DoodleBeaker className="w-40 h-40" />
                </motion.div>
                <motion.div
                  animate={{ y: [0, -8, 0], rotate: [0, 12, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute left-0 top-16 text-[#FFB703]"
                >
                  <DoodleStar className="w-14 h-14" />
                </motion.div>
                <motion.div
                  animate={{ x: [0, 6, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute left-10 bottom-4 text-black"
                >
                  <DoodleArrow className="w-24 h-16" />
                </motion.div>
                <div className="absolute right-0 bottom-0 text-black">
                  <DoodleFace className="w-24 h-24" />
                </div>
              </div>
            </FadeUp>
          </div>
        </div>

        <ul data-testid="services-list" className="border-t border-black">
          {SERVICES.map((s, i) => (
            <motion.li
              key={s.no}
              data-testid={`service-row-${s.no}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.05 }}
              className="group relative border-b border-black grid grid-cols-12 gap-4 py-6 sm:py-8 px-2 sm:px-4 transition-colors duration-300 hover:bg-black hover:text-[#fdfbf7]"
            >
              <span className="col-span-2 sm:col-span-1 font-mono text-[11px] sm:text-xs pt-2 opacity-70">
                {s.no}
              </span>
              <h3
                className="col-span-10 sm:col-span-6 font-display text-2xl sm:text-3xl lg:text-[40px] leading-[1] tracking-tight"
                style={{ fontWeight: 800 }}
              >
                {s.title}
              </h3>
              <p className="col-span-12 sm:col-span-4 text-[14px] sm:text-[15px] leading-relaxed opacity-80 sm:pt-2">
                {s.desc}
              </p>
              <div className="col-span-12 sm:col-span-1 flex sm:justify-end items-start pt-2">
                <Plus className="w-5 h-5 transition-transform duration-300 group-hover:rotate-90" />
              </div>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}
